import type { CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import { LabAddDialog } from '@/encounter/lab-add-dialog';
import { ImagingAddDialog } from '@/encounter/imaging-add-dialog';
import { ProcedureAddDialog } from '@/encounter/procedure-add-dialog';
import { MedicationDoseDialog } from '@/encounter/medication-dose-dialog';
import { VaccineAddDialog } from '@/encounter/vaccine-add-dialog';
import type { CodedOrderPayload } from '@/encounter/lib/order-payload';
import { toMedicationPick } from '@/encounter/lib/to-medication-result';
import {
  payloadToCodeSearchResult,
  payloadToMedicationDose,
  payloadToMedicationPick,
  vaccinationEntryToDraft,
  vaccinationEntryToValueSet,
} from '@/encounter/lib/payload-to-seed';
import type { OrderSearchPick } from '@/settings/services/use-order-set-order-search';
import { payloadToItem, type ItemFromPayload } from './order-set-item-details';

/**
 * The A/P's own order form, opened from the order set builder (PROD-2714).
 *
 * The EditModal board: "The pencil opens the same order form as A/P; its details become the set's
 * defaults." Since PROD-2717 (Andrew, 2026-09-30) there is no pencil: pressing the row opens it. So this renders the very dialog the A/P opens for that order type (the lab popup, the
 * medication dose form, the imaging / procedure / vaccine dialogs), seeded the way the A/P seeds
 * it (`assessment-plan-view.tsx` openSeededPicker for a new pick, handleEditOrder for a pressed card),
 * with `authoring`: no patient, no encounter, and the visit's own fields hidden (DIAGNOSIS block,
 * dates, notes, lot numbers, weight). The interaction check needs a patient, so it runs when the
 * set is applied, not here.
 *
 * On Save the form's payload goes through `payloadToItem` (the A/P's own wire mapper) and comes
 * back as the item's `type`, `code` and `details`.
 */
export type OrderFormRequest =
  /** A search pick, opened on the form exactly as a rail pick is. Education has no form. */
  | { mode: 'add'; pick: Exclude<OrderSearchPick, { type: 'education' }> }
  /** A pressed row: the item's details read back into the form's own payload. */
  | { mode: 'edit'; payload: CodedOrderPayload };

export interface OrderSetOrderFormProps {
  request: OrderFormRequest;
  /** The practitioner, for the provider-scoped catalogues (the A/P's `providerId`). */
  providerId?: string;
  onSave: (item: ItemFromPayload) => void;
  onCancel: () => void;
}

export function OrderSetOrderForm({ request, providerId, onSave, onCancel }: OrderSetOrderFormProps) {
  const editing = request.mode === 'edit';
  const finish = (payload: CodedOrderPayload, label: string) => {
    const item = payloadToItem(payload, label);
    if (item) onSave(item);
    else onCancel();
  };
  const kind = request.mode === 'add' ? KIND_OF_PICK[request.pick.type] : request.payload.kind;
  const edit = request.mode === 'edit' ? request.payload : undefined;
  const pickItem = request.mode === 'add' ? request.pick.item : undefined;

  switch (kind) {
    case 'lab': {
      const lab = edit?.kind === 'lab' ? edit : undefined;
      return (
        <LabAddDialog
          authoring
          providerId={providerId}
          diagnoses={[]}
          initialItem={lab ? payloadToCodeSearchResult(lab) : (pickItem as CodeSearchResult)}
          initialSubTests={lab?.subTests}
          initialOmittedTests={lab?.omittedTests}
          initialVendor={lab?.vendor}
          initialPanelDefinitionId={lab?.panelDefinitionId}
          initialPanelVersion={lab?.panelVersion}
          initialBillTo={lab?.billTo}
          editing={editing}
          onConfirm={(o) => finish(o.payload, o.planOrder.label)}
          onCancel={onCancel}
        />
      );
    }
    case 'imaging':
      return (
        <ImagingAddDialog
          authoring
          providerId={providerId}
          diagnoses={[]}
          initialItem={edit ? payloadToCodeSearchResult(edit) : (pickItem as CodeSearchResult)}
          editing={editing}
          onConfirm={(o) => finish(o.payload, o.planOrder.label)}
          onCancel={onCancel}
        />
      );
    case 'procedure': {
      const proc = edit?.kind === 'procedure' ? edit : undefined;
      return (
        <ProcedureAddDialog
          authoring
          providerId={providerId}
          diagnoses={[]}
          initialItem={
            proc ? { ...payloadToCodeSearchResult(proc)!, system: proc.system } : (pickItem as CodeSearchResult)
          }
          initialAddToBilling={proc?.addToBilling}
          editing={editing}
          onConfirm={(o) => finish(o.payload, o.planOrder.label)}
          onCancel={onCancel}
        />
      );
    }
    case 'medication': {
      const med = edit?.kind === 'medication' ? edit : undefined;
      return (
        <MedicationDoseDialog
          authoring
          dxOptions={[]}
          medication={med ? payloadToMedicationPick(med) : toMedicationPick(pickItem)}
          initialDose={med ? payloadToMedicationDose(med) : undefined}
          providerId={providerId}
          editing={editing}
          onConfirm={(o) => finish(o.payload, o.planOrder.label)}
          onCancel={onCancel}
        />
      );
    }
    case 'vaccination': {
      const vax = edit?.kind === 'vaccination' ? edit : undefined;
      return (
        <VaccineAddDialog
          authoring
          dxOptions={[]}
          initialValueSet={vax ? vaccinationEntryToValueSet(vax.entry) : (pickItem as Extract<OrderSearchPick, { type: 'vaccine' }>['item'])}
          initialDraft={vax ? vaccinationEntryToDraft(vax.entry) : undefined}
          editing={editing}
          onConfirm={(o) => finish(o.payload, o.planOrder.label)}
          onCancel={onCancel}
        />
      );
    }
    default:
      return null;
  }
}

const KIND_OF_PICK = {
  lab: 'lab',
  imaging: 'imaging',
  procedure: 'procedure',
  medication: 'medication',
  vaccine: 'vaccination',
} as const satisfies Record<Exclude<OrderSearchPick['type'], 'education'>, CodedOrderPayload['kind']>;
